import type { Filter, MeasurementUnit } from '@cvent/venue-search-shared/types';
import { type RoomLayout } from '@/utils/filters/options/getRoomLayoutOptions';
import type { SpaceCategory, SpaceOptions } from '@/utils/filters/options/getSpaceOptions';
import dynamic from 'next/dynamic';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';

const SpaceMeasurementFilter = dynamic(() => import('@/components/filters/SpaceMeasurementFilter'), { ssr: false });
const SpaceCapacityFilter = dynamic(() => import('@/components/filters/SpaceCapacityFilter'), { ssr: false });
const RoomQuantitiesFilter = dynamic(() => import('@/components/filters/RoomQuantitiesFilter'), { ssr: false });

type Props = {
  spaceDetailsCategory: {
    spaceMeasurementsCategory: SpaceCategory[];
    spaceCapacityCategory: SpaceCategory[];
    roomQuantitiesCategory: SpaceCategory[];
  };
  selected: Filter;
  locale: Locale;
  setSelected: (selections: Filter) => void;
  labels: {
    title: string;
    maxMinError: string;
    wholeNumberRequired: string;
    howMuchSpace: string;
    calculateCapacity: string;
    capacityCalculatorTitle: string;
    capacityCalculatorRoomLayoutLabel: string;
    capacityCalculatorRearProjectionRequiredLabel: string;
    capacityCalculatorSocialDistancingLabel: string;
    capacityCalculatorAttendeesPerDay: string;
    capacityCalculatorSqFeetLabel: string;
    capacityCalculatorSqMetreLabel: string;
    capacityCalculatorFeetLabel: string;
    capacityCalculatorClickingApplyMsg: string;
    capacityCalculatorApplyLabel: string;
    capacityCalculatorCloseLabel: string;
    close: string;
    back: string;
  };
  spaceMeasurementsOption: SpaceOptions;
  spaceCapacityOption: SpaceOptions;
  roomLayoutOptions: Array<RoomLayout>;
  measurementUnit: MeasurementUnit;
  roomQuantitiesOption: SpaceOptions;
  errorLabels: Record<string, string>;
  testID?: string;
};

export default function SpaceDetailsFilter({
  selected,
  locale,
  setSelected,
  labels,
  spaceMeasurementsOption,
  spaceCapacityOption,
  roomLayoutOptions,
  roomQuantitiesOption,
  spaceDetailsCategory,
  measurementUnit,
  errorLabels,
  testID
}: Props): JSX.Element {
  const {
    title,
    maxMinError,
    wholeNumberRequired,
    howMuchSpace,
    calculateCapacity,
    capacityCalculatorTitle,
    capacityCalculatorRoomLayoutLabel,
    capacityCalculatorRearProjectionRequiredLabel,
    capacityCalculatorSocialDistancingLabel,
    capacityCalculatorAttendeesPerDay,
    capacityCalculatorSqFeetLabel,
    capacityCalculatorSqMetreLabel,
    capacityCalculatorFeetLabel,
    capacityCalculatorClickingApplyMsg,
    capacityCalculatorApplyLabel,
    capacityCalculatorCloseLabel,
    close,
    back
  } = labels;

  return (
    <div className="border-y border-neutral-20 py-3">
      <div className="mb-2 text-b-xl/loose font-medium">{title}</div>
      <RoomQuantitiesFilter
        options={spaceDetailsCategory.roomQuantitiesCategory}
        selected={selected}
        setSelected={setSelected}
        labels={{
          title: roomQuantitiesOption.title,
          maxMinError,
          wholeNumberRequired
        }}
        showTitle
      />
      {spaceDetailsCategory.roomQuantitiesCategory.length > 0 ? <div className="pb-4" /> : <></>}
      <SpaceMeasurementFilter
        options={spaceDetailsCategory.spaceMeasurementsCategory}
        selected={selected}
        setSelected={setSelected}
        labels={{
          title: formatMessage(spaceMeasurementsOption.title, locale, {
            count: 0
          }),
          subTitle: spaceMeasurementsOption.subTitle as string,
          close,
          back,
          maxMinError,
          wholeNumberRequired,
          howMuchSpace,
          calculateCapacity,
          capacityCalculatorTitle,
          capacityCalculatorRoomLayoutLabel,
          capacityCalculatorRearProjectionRequiredLabel,
          capacityCalculatorSocialDistancingLabel,
          capacityCalculatorAttendeesPerDay,
          capacityCalculatorSqFeetLabel,
          capacityCalculatorSqMetreLabel,
          capacityCalculatorFeetLabel,
          capacityCalculatorClickingApplyMsg,
          capacityCalculatorApplyLabel,
          capacityCalculatorCloseLabel
        }}
        showTitle
        showCalculateCapacity
        isSurfaceFilter={false}
        roomLayoutOptions={roomLayoutOptions}
        measurementUnit={measurementUnit}
        errorLabels={errorLabels}
        testID={testID}
      />
      {spaceDetailsCategory.spaceMeasurementsCategory.length > 0 ? <div className="pb-4" /> : <></>}
      <SpaceCapacityFilter
        options={spaceDetailsCategory.spaceCapacityCategory}
        selected={selected}
        setSelected={setSelected}
        labels={{
          title: spaceCapacityOption.title,
          subTitle: spaceCapacityOption.subTitle as string,
          maxMinError,
          wholeNumberRequired
        }}
        showTitle
        testID={testID}
      />
    </div>
  );
}
